<template>
  <div class="timeline-inspector">
    <div class="inspector-section">
      <div class="section-title">姿态搜索与配置</div>
      
      <div v-if="selectedNode" class="node-info mb-4">
        <div class="info-row">
          <span class="label">当前时间</span>
          <span class="value">{{ (selectedNode.sec).toFixed(1) }}s</span>
        </div>
        <div class="info-row">
          <span class="label">动作状态</span>
          <span class="value" :class="{ 'has-pose': selectedNode.norm_pose }">
            {{ selectedNode.norm_pose ? '已采集姿态' : '无姿态' }}
          </span>
        </div>
        
        <!-- Target Color Display -->
        <div class="info-row mt-2" v-if="selectedNode.upper_colors || selectedNode.lower_colors">
          <span class="label">服饰颜色</span>
          <div class="value flex-grow-1 ml-4 d-flex flex-column gap-1">
            <div class="color-mini-bar" v-if="selectedNode.upper_colors">
              <div v-for="(c, ci) in selectedNode.upper_colors" :key="ci" 
                   :style="{ flex: c.ratio, backgroundColor: bgrToHex(c.bgr) }"
                   :title="`${c.name}: ${(c.ratio*100).toFixed(0)}%`"
              ></div>
            </div>
            <div class="color-mini-bar" v-if="selectedNode.lower_colors">
              <div v-for="(c, ci) in selectedNode.lower_colors" :key="ci" 
                   :style="{ flex: c.ratio, backgroundColor: bgrToHex(c.bgr) }"
                   :title="`${c.name}: ${(c.ratio*100).toFixed(0)}%`"
              ></div>
            </div>
          </div>
        </div>
      </div>

      <div class="filter-group">
        <v-select
          label="过滤人物"
          v-model="internalFilterProfile"
          :items="profileOptions"
          density="compact"
          variant="outlined"
          class="mb-2"
        ></v-select>
        
        <v-switch
          v-model="internalScaleSensitive"
          label="大小敏感"
          color="primary"
          density="compact"
          hide-details
        ></v-switch>
        
        <v-switch
          v-model="internalPosSensitive"
          label="位置敏感"
          color="primary"
          density="compact"
          hide-details
        ></v-switch>

        <v-divider class="my-2"></v-divider>

        <v-switch
          v-model="internalFilterSameColor"
          label="过滤同色 (找同款不同色)"
          color="warning"
          density="compact"
          hide-details
        ></v-switch>

        <div v-if="internalFilterSameColor" class="px-2 pt-2">
          <div class="d-flex justify-space-between text-caption text-grey">
            <span>相似度阈值</span>
            <span>{{ (internalColorThreshold * 100).toFixed(0) }}%</span>
          </div>
          <v-slider
            v-model="internalColorThreshold"
            min="0.5"
            max="0.99"
            step="0.01"
            density="compact"
            hide-details
            color="warning"
          ></v-slider>
          <div class="text-caption text-grey-darken-1 text-center mt-n1">
            越高越严格 (只过滤极其接近的颜色)
          </div>
        </div>
      </div>

      <v-btn
        block
        color="info"
        class="mt-4"
        :disabled="!selectedNode?.norm_pose || searching"
        :loading="searching"
        @click="$emit('search')"
      >
        <v-icon start>mdi-magnify</v-icon>搜索相似姿态
      </v-btn>

      <div class="stats-text mt-2 text-center text-caption" :class="{ 'text-error': statsText.includes('失败') }">
        {{ statsText }}
      </div>
    </div>

    <v-divider class="my-4"></v-divider>

    <div class="results-section">
      <div class="section-title d-flex justify-space-between align-center">
        <span>搜索结果 ({{ results.length }})</span>
        <v-btn v-if="results.length" variant="text" size="x-small" color="error" @click="$emit('clear-results')">清除</v-btn>
      </div>
      
      <div class="results-list">
        <div 
          v-for="(res, idx) in results" 
          :key="idx" 
          class="result-item"
          @click="$emit('jump', res.sec)"
        >
          <div class="result-header">
            <span class="time">{{ res.sec.toFixed(1) }}s</span>
            <span class="profile">Person: {{ res.profileId }}</span>
          </div>
          <div class="result-details">
            Similarity: {{ (res.score * 100).toFixed(1) }}%
            <v-icon size="x-small" color="primary">mdi-chevron-right</v-icon>
          </div>
          
          <!-- Result Colors -->
          <div class="result-colors mt-1 d-flex flex-column gap-1" v-if="res.upper_colors || res.lower_colors">
            <div class="color-mini-bar mini" v-if="res.upper_colors">
              <div v-for="(c, ci) in res.upper_colors" :key="ci" 
                   :style="{ flex: c.ratio, backgroundColor: bgrToHex(c.bgr) }"></div>
            </div>
            <div class="color-mini-bar mini" v-if="res.lower_colors">
              <div v-for="(c, ci) in res.lower_colors" :key="ci" 
                   :style="{ flex: c.ratio, backgroundColor: bgrToHex(c.bgr) }"></div>
            </div>
          </div>
        </div>
        <div v-if="results.length === 0 && !searching" class="empty-state text-caption text-center mt-4 text-grey-lighten-1">
          暂无搜索结果
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  selectedNode: Object,
  searching: Boolean,
  statsText: String,
  filterProfile: String,
  profileOptions: Array,
  scaleSensitive: Boolean,
  posSensitive: Boolean,
  filterSameColor: Boolean,
  colorThreshold: {
    type: Number,
    default: 0.8
  },
  results: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits([
  'update:filterProfile', 
  'update:scaleSensitive', 
  'update:posSensitive',
  'update:filterSameColor',
  'update:colorThreshold',
  'search',
  'clear-results',
  'jump'
])

const internalFilterProfile = computed({
  get: () => props.filterProfile,
  set: (val) => emit('update:filterProfile', val)
})

const internalScaleSensitive = computed({
  get: () => props.scaleSensitive,
  set: (val) => emit('update:scaleSensitive', val)
})

const internalPosSensitive = computed({
  get: () => props.posSensitive,
  set: (val) => emit('update:posSensitive', val)
})

const internalFilterSameColor = computed({
  get: () => props.filterSameColor,
  set: (val) => emit('update:filterSameColor', val)
})

const internalColorThreshold = computed({
  get: () => props.colorThreshold,
  set: (val) => emit('update:colorThreshold', val)
})

const bgrToHex = (bgr) => {
  if (!bgr || bgr.length < 3) return '#000'
  return "#" + (1 << 24 | bgr[2] << 16 | bgr[1] << 8 | bgr[0]).toString(16).slice(1)
}
</script>

<style scoped>
.timeline-inspector {
  padding: 16px;
  background-color: #242424;
  height: 100%;
  overflow-y: auto;
}

.section-title {
  color: #eee;
  font-size: 14px;
  font-weight: bold;
  text-transform: uppercase;
  margin-bottom: 16px;
  letter-spacing: 0.5px;
  text-align: center;
  border-bottom: 1px solid #333;
  padding-bottom: 8px;
}

:deep(.v-label) {
  color: #aaa !important;
  opacity: 1 !important;
  font-size: 14px;
}

:deep(.v-field__outline) {
  color: #555 !important;
}

:deep(.v-select__selection-text) {
  color: #fff !important;
  font-size: 14px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
  font-size: 14px;
}
.info-row .label { color: #aaa; }
.info-row .value { color: #fff; }
.value.has-pose { color: #00d2d3; font-weight: bold; }

.stats-text {
  color: #00d2d3;
}

.results-list {
  margin-top: 8px;
}

.result-item {
  padding: 8px;
  border-radius: 4px;
  background: #1a1a1a;
  margin-bottom: 6px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.2s;
}
.result-item:hover {
  background: #2a2a2a;
  border-color: #444;
}

.result-header {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  font-weight: bold;
}
.result-header .time { color: #00d2d3; }
.result-header .profile { color: #eee; }

.result-details {
  font-size: 10px;
  color: #aaa;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 4px;
}

.color-mini-bar {
  display: flex;
  height: 6px;
  border-radius: 2px;
  overflow: hidden;
  background: #333;
  width: 100%;
}

.color-mini-bar.mini {
  height: 4px;
}

.gap-1 {
  gap: 2px;
}

.flex-grow-1 {
  flex-grow: 1;
}
</style>
